import LoginIcon from '@mui/icons-material/Login';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  LinearProgress,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLoginMutation, useVerifyLogin2faMutation, LoginResponse } from '../auth/api/authApi';
import { MitarbeiterRole } from '../auth/api/types';
import { DatenschutzLoginDialog } from '../datenschutz/DatenschutzLoginDialog';

export const LoginScreen: FC = () => {
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Bitte überprüfen Sie Ihre Eingaben!');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials');
  const [tmpToken, setTmpToken] = useState<string | null>(null);
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [verifyLogin2fa, { isLoading: isVerifyLoading }] = useVerifyLogin2faMutation();

  const isLoading = isLoginLoading || isVerifyLoading;

  const passwordInputRef = useRef<HTMLInputElement>(null);
  const twoFactorInputRef = useRef<HTMLInputElement>(null);

  const handleCancelMfa = () => {
    setStep('credentials');
    setTmpToken(null);
    setTwoFactorCode('');
  };

  const handleLogin = () => {
    if (step === 'credentials') {
      login({
        username: email,
        password,
      })
        .unwrap()
        .then(res => {
          if ('requiresTwoFactor' in res && res.requiresTwoFactor) {
            setStep('mfa');
            setTmpToken(res.tmpToken);
            setTwoFactorCode('');
          } else if (res) {
            const payload = res as Extract<LoginResponse, { access_token: string }>;
            const from = payload.role === MitarbeiterRole.EXTERN ? '/extern' : '/intern';

            // Workaround for race condition
            setTimeout(() => navigate(from, { replace: true }), 1000);
          }
        })
        .catch(err => {
          let msg = 'Bitte überprüfen Sie Ihre Eingaben!';
          if (err?.status === 429) {
            msg = 'Zu viele Versuche. Bitte warten Sie 5 Minuten, bevor Sie es erneut versuchen.';
          } else if (err?.status === 401) {
            msg = 'Ungültige Anmeldedaten.';
          } else if (err?.data?.message) {
            msg = Array.isArray(err.data.message) ? err.data.message[0] : err.data.message;
          }
          setErrorMessage(msg);
          setSnackBarOpen(true);
        });
    } else {
      if (!twoFactorCode || twoFactorCode.length !== 6 || !tmpToken) {
        setErrorMessage('Bitte geben Sie einen gültigen 6-stelligen Code ein.');
        setSnackBarOpen(true);
        return;
      }
      verifyLogin2fa({ code: twoFactorCode, tmpToken })
        .unwrap()
        .then(res => {
          if (res) {
            const from = res.role === MitarbeiterRole.EXTERN ? '/extern' : '/intern';

            // Workaround for race condition
            setTimeout(() => navigate(from, { replace: true }), 1000);
          }
        })
        .catch(err => {
          let msg = 'Anmeldung fehlgeschlagen.';
          if (err?.status === 429) {
            msg = 'Zu viele Versuche. Bitte warten Sie 5 Minuten, bevor Sie es erneut versuchen.';
          } else if (err?.status === 401) {
            msg = 'Ungültiger 2FA-Code oder Sitzung abgelaufen.';
          } else if (err?.data?.message) {
            msg = Array.isArray(err.data.message) ? err.data.message[0] : err.data.message;
          }
          setErrorMessage(msg);
          setSnackBarOpen(true);
        });
    }
  };

  useEffect(() => {
    if (step === 'mfa') {
      twoFactorInputRef.current?.focus();
    }
  }, [step]);

  return (
    <Stack
      spacing={0}
      direction="column"
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'grey.50',
      }}
    >
      <Container maxWidth={'xs'}>
        {isLoading && <LinearProgress />}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar>
            <LoginIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            {step === 'mfa' ? 'Zwei-Faktor-Authentifizierung' : 'Anmelden'}
          </Typography>
          <Box sx={{ width: '100%', mt: 1 }}>
            {step === 'credentials' && (
              <>
                <TextField
                  onKeyDown={ev => {
                    if (ev.key === 'Enter') {
                      passwordInputRef.current?.focus();
                      ev.preventDefault();
                    }
                  }}
                  margin="normal"
                  required
                  fullWidth
                  label="Email"
                  type="email"
                  onChange={e => setEmail(e.target.value)}
                  value={email}
                />
                <TextField
                  inputRef={passwordInputRef}
                  onKeyDown={ev => {
                    if (ev.key === 'Enter') {
                      handleLogin();
                      ev.preventDefault();
                    }
                  }}
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  type="password"
                  onChange={e => setPassword(e.target.value)}
                  value={password}
                />
              </>
            )}
            {step === 'mfa' && (
              <TextField
                inputRef={twoFactorInputRef}
                margin="normal"
                required
                fullWidth
                label="6-stelliger 2FA Code"
                type="text"
                slotProps={{
                  htmlInput: {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 6,
                  },
                }}
                value={twoFactorCode}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTwoFactorCode(val);
                }}
                onKeyDown={ev => {
                  if (ev.key === 'Enter') {
                    handleLogin();
                    ev.preventDefault();
                  }
                }}
                helperText="Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein"
              />
            )}
            <Snackbar
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              open={snackBarOpen}
              autoHideDuration={6000}
              onClose={() => setSnackBarOpen(false)}
            >
              <Alert severity="error" onClose={() => setSnackBarOpen(false)}>
                <AlertTitle>Anmeldung fehlgeschlagen!</AlertTitle>
                {errorMessage}
              </Alert>
            </Snackbar>
            <Button
              variant="contained"
              onClick={handleLogin}
              disabled={isLoading || (step === 'mfa' && twoFactorCode.length !== 6)}
              fullWidth
              sx={{ mt: 2, mb: 1 }}
            >
              {step === 'mfa' ? 'Code bestätigen' : 'Anmelden'}
            </Button>
            {step === 'mfa' && (
              <Button
                variant="outlined"
                onClick={handleCancelMfa}
                disabled={isLoading}
                fullWidth
                sx={{ mb: 1 }}
              >
                Zurück zur Anmeldung
              </Button>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <DatenschutzLoginDialog />
              <Link href="https://auriscon.info/impressum" target="_blank" underline="none">
                Impressum
              </Link>
            </Box>
          </Box>
        </Box>
      </Container>
    </Stack>
  );
};
